import { useState, useEffect } from "react";
import {
  upsertAddress,
  getUserDetails,
  updateProfile,
  sendPasswordResetLink,
} from "../../api/user";
import { COUNTRY_CODES } from "../../classes/Enum";
import { useAuth } from "../AuthProvider";
import {
  formatAddressLines,
  getAuStates,
  normalizeAddressFields,
  validateShippingAddress,
} from "../../helper/address";
import "../../styles/profile.css";

function formatBirthdayDisplay(isoDate) {
  if (!isoDate) return "";
  const [year, month, day] = isoDate.split("-");
  if (!year || !month || !day) return isoDate;
  return `${day}/${month}/${year}`;
}

const ProfileHeader = ({ user }) => (
  <div className="profile-header row">
    <img className="profile-avatar" src="/avatars/avatar.png" alt="User profile" />
    <div className="profile-header-text">
      <h1>
        {user?.firstName} {user?.lastName}
      </h1>
      {user?.isSubscriber && <span className="profile-badge">Subscriber</span>}
    </div>
  </div>
);

const VerificationBanner = ({ isVerified }) => {
  if (isVerified) return null;
  return (
    <div className="profile-banner profile-banner-warning">
      Your email is not verified yet. Check your inbox for the verification link.
    </div>
  );
};

const AccountDetailsSection = ({ details, setDetails, refreshMe }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [tiktokName, setTiktokName] = useState("");

  useEffect(() => {
    if (!details) return;
    setFirstName(details.firstName || "");
    setLastName(details.lastName || "");
    setTiktokName(details.tiktokName || "");
  }, [details]);

  const isActive =
    firstName.trim() !== "" &&
    lastName.trim() !== "" &&
    tiktokName.trim() !== "";

  async function handleSave() {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      await updateProfile({
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        tiktok_name: tiktokName.trim(),
      });
      setSuccess("Account details updated.");
      const updated = await getUserDetails();
      setDetails(updated);
      await refreshMe();
      setIsExpanded(false);
    } catch (err) {
      setError(err?.message || "Failed to update account details.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="profile-section">
      <div className="profile-section-header row">
        <h2>Account details</h2>
        <button
          type="button"
          className="button"
          onClick={() => setIsExpanded((v) => !v)}
        >
          {isExpanded ? "Hide" : "Edit"}
        </button>
      </div>

      {error && <div className="error">{error}</div>}
      {success && <div className="success">{success}</div>}

      <dl className="profile-details-list">
        <div>
          <dt>Email</dt>
          <dd>{details?.email || "—"}</dd>
        </div>
        <div>
          <dt>Name</dt>
          <dd>
            {details?.firstName} {details?.lastName}
          </dd>
        </div>
        <div>
          <dt>TikTok</dt>
          <dd>{details?.tiktokName ? `@${details.tiktokName}` : "Not set"}</dd>
        </div>
        <div>
          <dt>Birthday</dt>
          <dd>{formatBirthdayDisplay(details?.birthday) || "Not set"}</dd>
        </div>
      </dl>

      {isExpanded && (
        <div className="user-details profile-form">
          <label className="label">
            First name
            <input
              className="input-box"
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
          </label>

          <label className="label">
            Last name
            <input
              className="input-box"
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
          </label>

          <label className="label">
            TikTok username
            <input
              className="input-box"
              type="text"
              value={tiktokName}
              onChange={(e) => setTiktokName(e.target.value)}
              placeholder="without @"
            />
          </label>

          <button
            className={`button ${isActive ? "active" : ""}`}
            disabled={!isActive || saving}
            onClick={handleSave}
          >
            {saving ? "Saving..." : "Save details"}
          </button>
        </div>
      )}
    </section>
  );
};

const FieldHint = ({ children }) => (
  <span className="profile-field-hint">{children}</span>
);

const FieldError = ({ message }) =>
  message ? <span className="profile-field-error">{message}</span> : null;

const AddressPreviewCard = ({ fields }) => {
  const lines = formatAddressLines(fields);
  if (!lines.length) return null;

  return (
    <div className="address-preview-card">
      <p className="address-preview-label">Delivery address preview</p>
      {lines.map((line) => (
        <p key={line} className="address-preview-line">
          {line}
        </p>
      ))}
    </div>
  );
};

const AddressSection = ({ refreshMe }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [touched, setTouched] = useState({});

  const [address, setAddress] = useState("");
  const [suburb, setSuburb] = useState("");
  const [postcode, setPostcode] = useState("");
  const [state, setState] = useState("");
  const [country, setCountry] = useState("AU");

  const formValues = { address, suburb, postcode, state, country };
  const validation = validateShippingAddress(formValues);
  const isAu = country === "AU";

  function markTouched(field) {
    setTouched((prev) => ({ ...prev, [field]: true }));
  }

  function validateField(field) {
    const result = validateShippingAddress(formValues);
    setFieldErrors((prev) => ({
      ...prev,
      [field]: result.errors[field] || null,
    }));
  }

  function validateAll() {
    const result = validateShippingAddress(formValues);
    setFieldErrors(result.errors);
    setTouched({
      address: true,
      suburb: true,
      postcode: true,
      state: true,
      country: true,
    });
    return result;
  }

  useEffect(() => {
    async function fetchUserData() {
      try {
        setLoading(true);
        const userDetails = await getUserDetails();
        if (userDetails?.address) {
          const normalized = normalizeAddressFields(userDetails.address);
          setAddress(normalized.address);
          setSuburb(normalized.suburb);
          setPostcode(normalized.postcode);
          setState(normalized.state);
          setCountry(normalized.country || "AU");
        }
      } catch (err) {
        setError("Failed to load address.");
      } finally {
        setLoading(false);
      }
    }
    fetchUserData();
  }, []);

  async function handleUpdateAddress() {
    const result = validateAll();
    if (!result.ok) {
      setError("Please fix the highlighted fields before saving.");
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      await upsertAddress(result.data);
      setAddress(result.data.address);
      setSuburb(result.data.suburb);
      setPostcode(result.data.postcode);
      setState(result.data.state);
      setCountry(result.data.country);
      setSuccess("Address saved. Please double-check it matches where you receive mail.");
      await refreshMe();
      setIsExpanded(false);
    } catch (err) {
      setError(err?.message || "Failed to update address.");
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="profile-section">Loading address...</div>;

  const showPreview = validation.ok && (address || suburb || postcode);

  return (
    <section className="profile-section">
      <div className="profile-section-header row">
        <h2>Shipping address</h2>
        <button
          type="button"
          className="button"
          onClick={() => setIsExpanded((v) => !v)}
        >
          {isExpanded ? "Hide" : showPreview ? "Edit" : "Add"}
        </button>
      </div>

      <p className="address-light profile-address-intro">
        Orders ship to this address. Use the same format Australia Post would accept on a parcel label.
      </p>

      {error && <div className="error">{error}</div>}
      {success && <div className="success">{success}</div>}

      {!isExpanded && (
        showPreview ? (
          <AddressPreviewCard fields={formValues} />
        ) : (
          <p className="address-light">No address saved yet — add one for faster checkout.</p>
        )
      )}

      {isExpanded && (
        <div className="user-details profile-form profile-address-form">
          <label className="label">
            Country
            <select
              value={country}
              onChange={(e) => {
                setCountry(e.target.value);
                setState("");
                markTouched("country");
                validateField("country");
              }}
              onBlur={() => {
                markTouched("country");
                validateField("country");
              }}
            >
              <option value="">Select country</option>
              {COUNTRY_CODES.map(({ code, name }) => (
                <option key={code} value={code}>
                  {name}
                </option>
              ))}
            </select>
            <FieldHint>Choose country first — field rules change based on destination.</FieldHint>
            {touched.country && <FieldError message={fieldErrors.country} />}
          </label>

          <label className="label">
            Street address
            <input
              className={`input-box${touched.address && fieldErrors.address ? " input-invalid" : ""}`}
              type="text"
              value={address}
              placeholder="e.g. 12 Example Street"
              autoComplete="address-line1"
              onChange={(e) => setAddress(e.target.value)}
              onBlur={() => {
                markTouched("address");
                validateField("address");
              }}
            />
            <FieldHint>Include unit or level if needed (e.g. Unit 3, 12 Example St). No PO boxes.</FieldHint>
            {touched.address && <FieldError message={fieldErrors.address} />}
          </label>

          <label className="label">
            Suburb / city
            <input
              className={`input-box${touched.suburb && fieldErrors.suburb ? " input-invalid" : ""}`}
              type="text"
              value={suburb}
              placeholder={isAu ? "e.g. Preston" : "City or suburb"}
              autoComplete="address-level2"
              onChange={(e) => setSuburb(e.target.value)}
              onBlur={() => {
                markTouched("suburb");
                validateField("suburb");
              }}
            />
            {touched.suburb && <FieldError message={fieldErrors.suburb} />}
          </label>

          <div className="profile-address-row">
            <label className="label">
              {isAu ? "State" : "State / region"}
              {isAu ? (
                <select
                  className={touched.state && fieldErrors.state ? "input-invalid" : ""}
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                  onBlur={() => {
                    markTouched("state");
                    validateField("state");
                  }}
                >
                  <option value="">Select state</option>
                  {getAuStates().map((code) => (
                    <option key={code} value={code}>
                      {code}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  className={`input-box${touched.state && fieldErrors.state ? " input-invalid" : ""}`}
                  type="text"
                  value={state}
                  placeholder={country === "US" ? "e.g. CA" : "State or region"}
                  autoComplete="address-level1"
                  onChange={(e) => setState(e.target.value)}
                  onBlur={() => {
                    markTouched("state");
                    validateField("state");
                  }}
                />
              )}
              {touched.state && <FieldError message={fieldErrors.state} />}
            </label>

            <label className="label">
              Postcode
              <input
                className={`input-box${touched.postcode && fieldErrors.postcode ? " input-invalid" : ""}`}
                type="text"
                inputMode={isAu || country === "NZ" ? "numeric" : "text"}
                maxLength={isAu || country === "NZ" ? 4 : 10}
                value={postcode}
                placeholder={isAu ? "3072" : "Postcode"}
                autoComplete="postal-code"
                onChange={(e) => setPostcode(e.target.value)}
                onBlur={() => {
                  markTouched("postcode");
                  validateField("postcode");
                }}
              />
              {touched.postcode && <FieldError message={fieldErrors.postcode} />}
            </label>
          </div>

          {showPreview && <AddressPreviewCard fields={formValues} />}

          <button
            className={`button ${validation.ok ? "active" : ""}`}
            disabled={!validation.ok || saving}
            onClick={handleUpdateAddress}
          >
            {saving ? "Saving..." : "Save address"}
          </button>
        </div>
      )}
    </section>
  );
};

const PasswordSection = ({ email }) => {
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  async function handleResetLink() {
    if (!email) return;
    try {
      setSending(true);
      setError(null);
      setMessage(null);
      await sendPasswordResetLink(email);
      setMessage("Password reset link sent to your email.");
    } catch (err) {
      setError(err?.message || "Failed to send reset link.");
    } finally {
      setSending(false);
    }
  }

  return (
    <section className="profile-section">
      <h2>Password</h2>
      <p className="address-light">
        Need to change your password? We&apos;ll email you a secure reset link.
      </p>
      {error && <div className="error">{error}</div>}
      {message && <div className="success">{message}</div>}
      <button
        type="button"
        className="button"
        disabled={sending || !email}
        onClick={handleResetLink}
      >
        {sending ? "Sending..." : "Send password reset link"}
      </button>
    </section>
  );
};

const ProfileComponent = () => {
  const { user, refreshMe } = useAuth();
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const data = await getUserDetails();
        if (alive) setDetails(data);
      } catch (err) {
        if (alive) setError("Failed to load profile.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [user]);

  if (loading) {
    return (
      <div className="profile-scroll-container hide-scrollbar">
        <div className="profile-page">Loading profile...</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="profile-scroll-container hide-scrollbar">
        <div className="profile-page error">{error}</div>
      </div>
    );
  }

  const isVerified = details?.is_verified ?? user?.is_verified;

  return (
    <div className="profile-scroll-container hide-scrollbar">
      <div className="profile-page">
        <ProfileHeader user={user} />
        <VerificationBanner isVerified={isVerified} />
        <AccountDetailsSection details={details} setDetails={setDetails} refreshMe={refreshMe} />
        <AddressSection refreshMe={refreshMe} />
        <PasswordSection email={details?.email || user?.email} />
      </div>
    </div>
  );
};

export default ProfileComponent;
